import { type ApiContainerAction, EnumContainerActionStatus, EnumContainerOrderAction } from '@Model';
import { type PlannedContainerEntry } from './plannedContainerEntries';

export interface ConfirmableLines {
    openCount: number;
    confirmableCount: number;
    actionIds: string[];
}

export type ContainerLineState = 'open' | 'confirmed' | 'booked';

export type ContainerLineField = 'type' | 'content' | 'number';

// @ai-generated 2026-10-15T07:18:24+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
export function lineState(status: EnumContainerActionStatus): ContainerLineState {
    switch (status) {
        case EnumContainerActionStatus.Booked:
            return 'booked';
        case EnumContainerActionStatus.ToBeBooked:
            return 'confirmed';
        default:
            return 'open';
    }
}

/** Why the line cannot be confirmed yet. */
// @ai-generated 2026-10-15T07:18:24+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
export function whyLineCannotBeConfirmed(
    actions: readonly ApiContainerAction[],
    isIndividual: (action: ApiContainerAction) => boolean,
): string | undefined {
    if (actions.some((action) => !action.container_type.foreign_key)) {
        return 'Erst den Containertyp wählen.';
    }

    if (actions.some((action) => isIndividual(action) && !action.container.foreign_key)) {
        return 'Erst die Nummer wählen.';
    }

    return undefined;
}

// @ai-generated 2026-10-15T07:18:24+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
export function confirmableLines(
    entries: readonly PlannedContainerEntry[],
    isIndividual: (action: ApiContainerAction) => boolean,
): ConfirmableLines {
    const lines = entries.flatMap((entry) => {
        if (entry.kind === 'collective') {
            const open = entry.group.actions.filter((action) => lineState(action.booking_status) === 'open');
            return open.length > 0 ? [{ rows: open, ids: open.map((action) => action.id) }] : [];
        }

        const rows = entry.kind === 'exchange' ? [entry.pickup, entry.delivery] : [entry.action];
        const lead = rows[0];
        return lineState(lead.booking_status) === 'open' ? [{ rows, ids: [lead.id] }] : [];
    });

    const complete = lines.filter((line) => whyLineCannotBeConfirmed(line.rows, isIndividual) === undefined);

    return {
        openCount: lines.length,
        confirmableCount: complete.length,
        actionIds: complete.flatMap((line) => line.ids),
    };
}

/** The next empty field of an open row: type, content, then the number once completed. */
// @ai-generated 2026-10-15T07:18:24+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
export function fieldToOpen(
    action: ApiContainerAction,
    individual: boolean,
    completing: boolean,
): ContainerLineField | undefined {
    if (lineState(action.booking_status) !== 'open') {
        return undefined;
    }

    if (!action.container_type.foreign_key) {
        return 'type';
    }

    if (!action.content.foreign_key) {
        return 'content';
    }

    return individual && completing && !action.container.foreign_key ? 'number' : undefined;
}

export interface ContainerLineTexts {
    confirm: string;
    confirmed: string;
}

// @ai-generated 2026-10-15T07:18:24+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
export function containerLineTexts(action: EnumContainerOrderAction | 'swap'): ContainerLineTexts {
    switch (action) {
        case 'swap':
            return { confirm: 'Als getauscht bestätigen', confirmed: 'getauscht bestätigt' };
        case EnumContainerOrderAction.Collect:
            return { confirm: 'Als abgeholt bestätigen', confirmed: 'abgeholt bestätigt' };
        case EnumContainerOrderAction.Empty:
            return { confirm: 'Als geleert bestätigen', confirmed: 'geleert bestätigt' };
        default:
            return { confirm: 'Als gestellt bestätigen', confirmed: 'gestellt bestätigt' };
    }
}
